import React, { useCallback, useEffect, useRef, useState } from 'react';

const POS_KEY = 'fiche_background_toast_pos';
const PANEL_WIDTH = 360;

export function getFicheScrapeDisplay(progress) {
  const visible = Boolean(progress?.enabled && (progress?.active || progress?.complete));
  const total = Number(progress?.total) || 0;
  const current = Number(progress?.current) || 0;
  const complete = Boolean(progress?.complete) && !progress?.active;
  const percent = complete
    ? 100
    : (total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0);
  const percentLabel = complete ? '100%' : (total > 0 ? `${percent}%` : '…');
  const currentTitle = String(progress?.currentTitle || '').trim();
  const queueTitle = String(progress?.queueCurrentTitle || '').trim();
  const modelName = currentTitle || queueTitle;
  const hoverName = queueTitle || currentTitle
    || (complete ? 'Background scrape complete' : 'Background scrape running');
  return {
    visible,
    complete,
    percent,
    percentLabel,
    modelName,
    hoverName,
    current,
    total,
    queueLength: Number(progress?.queueLength) || 0,
    canStop: Boolean(progress?.canStop),
    processedCount: Number(progress?.processedCount) || 0,
    failedCount: Number(progress?.failedCount) || 0,
  };
}

function loadSavedPos() {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const left = Number(parsed?.left);
    const top = Number(parsed?.top);
    if (!Number.isFinite(left) || !Number.isFinite(top)) return null;
    return { left, top };
  } catch {
    return null;
  }
}

function clampPos(pos, size) {
  if (!pos) return null;
  const width = size?.width || PANEL_WIDTH;
  const height = size?.height || 140;
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(8, window.innerHeight - height - 8);
  return {
    left: Math.min(Math.max(8, pos.left), maxLeft),
    top: Math.min(Math.max(8, pos.top), maxTop),
  };
}

export function BackgroundScrapeChip({ progress, minimized, onRestore }) {
  const display = getFicheScrapeDisplay(progress);
  if (!display.visible || !minimized) return null;

  return (
    <button
      type="button"
      className={`pim-topbar__scrape-chip${display.complete ? ' is-complete' : ''}`}
      data-hover={display.hoverName}
      title={display.hoverName}
      onClick={onRestore}
      aria-label={`Background scrape ${display.percentLabel}. ${display.hoverName}. Click to restore.`}
    >
      <span className="pim-topbar__scrape-chip-dot" aria-hidden="true" />
      <span>Scrape {display.percentLabel}</span>
    </button>
  );
}

export default function BackgroundScrapeToast({
  progress,
  minimized,
  onMinimize,
  onOpenProgress,
  onStop,
  onOpenMapping,
}) {
  const display = getFicheScrapeDisplay(progress);
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const [pos, setPos] = useState(() => loadSavedPos());

  const persistPos = useCallback((next) => {
    setPos(next);
    try {
      if (next) localStorage.setItem(POS_KEY, JSON.stringify(next));
      else localStorage.removeItem(POS_KEY);
    } catch {}
  }, []);

  useEffect(() => {
    if (!pos) return undefined;
    const onResize = () => {
      const rect = panelRef.current?.getBoundingClientRect();
      persistPos(clampPos(pos, rect));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [pos, persistPos]);

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest('button')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const rect = panelRef.current?.getBoundingClientRect();
    persistPos(clampPos({
      left: event.clientX - dragRef.current.offsetX,
      top: event.clientY - dragRef.current.offsetY,
    }, rect));
  };

  const onPointerUp = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch {}
  };

  if (!display.visible || minimized) return null;

  const style = pos
    ? { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' }
    : { right: 16, bottom: 16 };

  return (
    <div
      ref={panelRef}
      className={`pim-scrape-toast${display.complete ? ' is-complete' : ''}`}
      style={style}
    >
      <div
        className="pim-scrape-toast__header"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="pim-scrape-toast__title">
          {display.complete ? 'Background Scrape Complete' : 'Background Scrape Running'}
        </div>
        <button
          type="button"
          className="pim-scrape-toast__icon-btn"
          title="Minimise to top bar"
          aria-label="Minimise to top bar"
          onClick={onMinimize}
        >
          –
        </button>
      </div>
      <div className="pim-scrape-toast__meta">
        {display.complete
          ? `${display.processedCount} items${display.failedCount ? ` • ${display.failedCount} failed` : ''}`
          : `${display.current}/${display.total || 0} • ${display.percentLabel}${display.queueLength ? ` • Queue: ${display.queueLength}` : ''}`}
      </div>
      {display.modelName ? (
        <div className="pim-scrape-toast__model" title={display.modelName}>
          {display.modelName}
        </div>
      ) : null}
      <div className="pim-scrape-toast__bar" aria-hidden="true">
        <div className="pim-scrape-toast__bar-fill" style={{ width: `${display.percent}%` }} />
      </div>
      <div className="pim-scrape-toast__actions">
        <button type="button" onClick={onOpenProgress}>Open Progress</button>
        {display.canStop ? (
          <button type="button" className="is-danger" onClick={onStop}>Stop</button>
        ) : null}
        <button type="button" onClick={onOpenMapping}>Mapping</button>
      </div>
    </div>
  );
}
